import React from "react";
import { Linking, Platform, Pressable, Text, View } from "react-native";

import type {
  TDocumentationAudioMetadata,
  TDocumentationPatientNote,
} from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { styles } from "../HomeConversations.styles";
import { toVoiceNoteFromPatientNote } from "../utils/parsers";
import ConsultNoteSummary from "./ConsultNoteSummary";

type PatientPhoneCallNotesCardProps = {
  patientNotes: TDocumentationPatientNote[];
  patientAudio: TDocumentationAudioMetadata[];
  formatDateTime: (value?: string | null) => string;
};

const PatientPhoneCallNotesCard: React.FC<PatientPhoneCallNotesCardProps> = ({
  patientNotes,
  patientAudio,
  formatDateTime,
}) => {
  const [expandedPatientNoteSummaries, setExpandedPatientNoteSummaries] =
    React.useState<Record<string, boolean>>({});
  const handleTogglePatientNoteSummary = React.useCallback((noteId: string) => {
    setExpandedPatientNoteSummaries((currentSummaries) => ({
      ...currentSummaries,
      [noteId]: !currentSummaries[noteId],
    }));
  }, []);

  const audioById = React.useMemo(() => {
    const nextMap = new Map<number, TDocumentationAudioMetadata>();
    patientAudio.forEach((audioItem) => {
      nextMap.set(audioItem.id, audioItem);
    });
    return nextMap;
  }, [patientAudio]);

  const notesWithRecording = React.useMemo(
    () =>
      patientNotes
        .map((note) => {
          const linkedAudio =
            (note.audio_info || []).find(
              (audioItem) => audioItem.type === "phone_call_audio",
            ) ||
            (note.audio_info || []).find((audioItem) => {
              const matchedAudio = audioById.get(audioItem.id);
              return matchedAudio?.type === "phone_call_audio";
            }) ||
            null;

          if (!linkedAudio) {
            return null;
          }

          const metadataAudio = audioById.get(linkedAudio.id);
          const recordingUrl =
            (linkedAudio.get_url || "").trim() ||
            (metadataAudio?.get_url || "").trim() ||
            null;

          return {
            note,
            recordingUrl,
          };
        })
        .filter(
          (
            item,
          ): item is {
            note: TDocumentationPatientNote;
            recordingUrl: string | null;
          } => !!item,
        ),
    [audioById, patientNotes],
  );

  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>All Patient Notes</Text>
      {!notesWithRecording.length ? (
        <Text style={styles.emptyText}>No patient notes found.</Text>
      ) : (
        <View style={styles.listContainer}>
          {notesWithRecording.map(({ note, recordingUrl }, noteIndex) => (
            <View key={note.id} style={styles.noteRow}>
              {recordingUrl && Platform.OS === "web" ? (
                <View style={styles.recordingPlayerContainer}>
                  {React.createElement("audio" as any, {
                    controls: true,
                    preload: "metadata",
                    src: recordingUrl,
                    style: {
                      width: "100%",
                      minHeight: 44,
                      borderRadius: ADMIN_TOKENS.radius.md,
                    } as any,
                  })}
                </View>
              ) : recordingUrl ? (
                <Pressable
                  style={[styles.callAgainButton, styles.recordingActionButton]}
                  onPress={() =>
                    Linking.openURL(recordingUrl).catch(() => undefined)
                  }
                  accessibilityLabel={`Open patient phone call audio for note ${note.id}`}
                >
                  <Text style={styles.callAgainButtonText}>Open Audio</Text>
                </Pressable>
              ) : null}
              <Text style={styles.callNumberText}>Note #{note.id}</Text>
              <Text style={styles.metaText}>
                {formatDateTime(note.created_at)}
              </Text>
              <ConsultNoteSummary
                note={toVoiceNoteFromPatientNote(note, noteIndex)}
                expandedSummaries={expandedPatientNoteSummaries}
                onToggleSummary={handleTogglePatientNoteSummary}
              />
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default PatientPhoneCallNotesCard;
